/**
 * A project billed in installments — the split and the spacing, decided on
 * screen before anything is written (the server re-checks the sum to the
 * cent: `backend/src/modules/invoices/installments.ts`).
 *
 * Pure. Dates are `YYYY-MM-DD` strings handled as calendar parts, never
 * `new Date(day)` — UTC midnight reads back as the previous day in Chicago.
 */

export type Spacing = 'monthly' | 'biweekly' | 'quarterly';

export const SPACING_OPTIONS: Array<{ value: Spacing; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'quarterly', label: 'Quarterly' },
];

/** Payment terms in days; 0 = due on receipt. */
export const TERMS_OPTIONS = [
  { value: '0', label: 'Due on receipt' },
  { value: '15', label: 'Net 15' },
  { value: '30', label: 'Net 30' },
  { value: '45', label: 'Net 45' },
];

export const MAX_INSTALLMENTS = 36;

export interface ScheduleRow {
  issueDate: string;
  /** Dollars, as typed — a string so a half-typed "1,2" is not lost. */
  amount: string;
}

function parts(day: string): [number, number, number] {
  const [y, m, d] = day.split('-').map(Number);
  return [y, m, d];
}

function fmt(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysIn(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * `k` months after `anchor`, clamped to the month's end and measured from the
 * ANCHOR, not chained — so a schedule starting on the 31st goes Jan 31 →
 * Feb 28 → Mar 31, back to the 31st rather than walking down the calendar.
 */
export function addMonths(anchor: string, k: number): string {
  const [y, m, d] = parts(anchor);
  const index = y * 12 + (m - 1) + k;
  const ny = Math.floor(index / 12);
  const nm = (index % 12) + 1;
  return fmt(ny, nm, Math.min(d, daysIn(ny, nm)));
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = parts(day);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return fmt(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function scheduleDates(first: string, count: number, spacing: Spacing): string[] {
  return Array.from({ length: count }, (_, i) =>
    spacing === 'biweekly' ? addDays(first, 14 * i) : addMonths(first, (spacing === 'quarterly' ? 3 : 1) * i),
  );
}

/** Money → integer cents; null when it is not an amount to the cent. */
export function toCents(value: string | number): number | null {
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n)) return null;
  const cents = Math.round(n * 100);
  return Math.abs(cents - n * 100) > 1e-6 ? null : cents;
}

/**
 * The total split into `count` EQUAL invoices, the odd cents on the LAST one —
 * $10,000 over 3 is 3,333.33 · 3,333.33 · 3,333.34, so every invoice but the
 * final one reads the same and the schedule still sums to the total exactly.
 */
export function splitEvenly(totalCents: number, count: number): number[] {
  if (count <= 0) return [];
  const each = Math.floor(totalCents / count);
  return Array.from({ length: count }, (_, i) => (i === count - 1 ? totalCents - each * (count - 1) : each));
}

export function buildSchedule(input: {
  totalCents: number;
  count: number;
  first: string;
  spacing: Spacing;
}): ScheduleRow[] {
  const dates = scheduleDates(input.first, input.count, input.spacing);
  return splitEvenly(input.totalCents, input.count).map((cents, i) => ({
    issueDate: dates[i],
    amount: (cents / 100).toFixed(2),
  }));
}
